import { FC } from "react";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import Column from "apps/website/components/layout/Column/Column";
import Container from "apps/website/components/layout/Container/Container";
import Grid from "apps/website/components/layout/Grid/Grid";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import List from "apps/website/components/base/List/List";
import Logo from "apps/website/components/base/Icon/Logo/Logo";
import Text from "apps/website/components/base/Text/Text";
import ListItem from "apps/website/components/base/ListItem/ListItem";
import NewsletterSignUp from "apps/website/components/feature/NewsletterSignUp/NewsletterSignUp";
import { PublicConfig } from "apps/website/config/public";
import { ROUTE_SIGN_UP_FLOW } from "apps/website/constants/routes";

import { ILink } from "../../base/Button/Button.map";

import FooterLinkGroup from "./FooterLinkGroup/FooterLinkGroup";
import FooterLink from "./FooterLink/FooterLink";
import { CountryToggle } from "./CountryToggle";
import { FOOTER_DATA, IFooterData, IFooterLinkGroup } from "./Footer.map";
import {
  addAssociatedDiscountToFooterLink,
} from "./add-associated-discount-to-footer-link";

// Helper function to transform a link if it matches the target path
const transformLinkIfNeeded = (link: ILink, newTo: string): ILink => {
  if (link.to === ROUTE_SIGN_UP_FLOW) {
    return { ...link, to: newTo };
  }
  return link;
};

// Helper function to get the target URL based on page type
const getTargetUrl = (
  isFreshFoodNewUserPage?: boolean,
  isFreshFeastTrialPage?: boolean,
  isSignupFlowV2AC?: boolean,
  isFreshFeastTrialACPage?: boolean,
  hasSUFv2CTAs?: boolean,
  isMetaLandingPage?: boolean,
  associatedDiscountCode: string = "discount_code=TRY20",
): string | null => {
  if (isFreshFoodNewUserPage) {
    return `${ROUTE_SIGN_UP_FLOW}?${associatedDiscountCode}`;
  }
  if (isFreshFeastTrialPage) {
    return ROUTE_SIGN_UP_FLOW;
  }
  if (isSignupFlowV2AC) {
    return `${ROUTE_SIGN_UP_FLOW}?discount_code=MEOWAC30`;
  }
  if (isFreshFeastTrialACPage) {
    return `${ROUTE_SIGN_UP_FLOW}?discount_code=MEOWAC30`;
  }
  if (hasSUFv2CTAs) {
    return `${ROUTE_SIGN_UP_FLOW}?${associatedDiscountCode}`;
  }
  if (isMetaLandingPage) {
    return `${PublicConfig.NEXT_PUBLIC_WEBSITE_URL}${ROUTE_SIGN_UP_FLOW}?discount_code=TRY20`;
  }
  return null;
};

export interface IFooter {
  className?: string;
  isMetaSignUpFunnelPage?: boolean
  isFreshFoodNewUserPage?: boolean
  isFreshFeastTrialPage?: boolean
  isFreshFeastTrialACPage?: boolean;
  isSignupFlowV2AC?: boolean;
  hasSUFv2CTAs?: boolean;
  associatedDiscountCode?: string;
}

export const footerDataTransformer = (
  footer: IFooterData,
  isMetaLandingPage?: boolean,
  isFreshFoodNewUserPage?: boolean,
  isFreshFeastTrialPage?: boolean,
  isSignupFlowV2AC?: boolean,
  isFreshFeastTrialACPage?: boolean,
  hasSUFv2CTAs?: boolean,
  associatedDiscountCode?: string,
): IFooterData => {
  const targetUrl = getTargetUrl(
    isFreshFoodNewUserPage,
    isFreshFeastTrialPage,
    isSignupFlowV2AC,
    isFreshFeastTrialACPage,
    hasSUFv2CTAs,
    isMetaLandingPage,
    associatedDiscountCode,
  );

  let secondaryLinkGroups: IFooterLinkGroup[] = footer.secondaryLinkGroups.map((group) => {
    if (group.title === "Try Fresh" && targetUrl) {
      return {
        ...group,
        links: group.links.map((link) => transformLinkIfNeeded(link, targetUrl)),
      };
    }
    return group;
  });

  if (!targetUrl && associatedDiscountCode) {
    secondaryLinkGroups = addAssociatedDiscountToFooterLink(secondaryLinkGroups, associatedDiscountCode);
  }

  return {
    ...footer,
    secondaryLinkGroups,
  };
};

const Footer: FC<IFooter> = ({
  className,
  isMetaSignUpFunnelPage,
  isFreshFoodNewUserPage,
  isFreshFeastTrialPage,
  isFreshFeastTrialACPage,
  isSignupFlowV2AC,
  hasSUFv2CTAs,
  associatedDiscountCode,
}) => {
  const { newsLetterSignUp,
    primaryLinkGroup,
    secondaryLinkGroups,
    tertiaryLinks } = footerDataTransformer(
    FOOTER_DATA,
    isMetaSignUpFunnelPage,
    isFreshFoodNewUserPage,
    isFreshFeastTrialPage,
    isSignupFlowV2AC,
    isFreshFeastTrialACPage,
    hasSUFv2CTAs,
    associatedDiscountCode,
  );

  const computedClasses = themeRootClassMap.footer;

  const [ secondaryLinkGroupList1, secondaryLinkGroupList2, ...secondaryLinkGroupListSecondary ] = secondaryLinkGroups;
  const secondaryLinkGroupListPrimary = [ secondaryLinkGroupList1, secondaryLinkGroupList2 ];

  return (
    <footer data-component="Footer" data-theme="footer" className={`${computedClasses} ${className}`}>
      <Spacer size="xl" />
      <Container>
        <Grid>
          <Column spans={{ lg: 1 }} className="hidden lg:block">&nbsp;</Column>
          <Column spans={{ lg: 5 }} className="flex">
            <NewsletterSignUp
              title={newsLetterSignUp.title}
              completeTitle={newsLetterSignUp.completeTitle}
              body={newsLetterSignUp.body}
              completeBody={newsLetterSignUp.completeBody}
              buttonText={newsLetterSignUp.buttonText}
              hideLabels={true}
              source="Footer"
            />
            <Spacer size="xl" />
          </Column>
          <Column spans={{ lg: 1 }} className="hidden lg:block">&nbsp;</Column>
          <Column spans={{ lg: 4 }}>
            <Grid gap="none">
              <>
                <Column className="lg:hidden ">
                  <FooterLinkGroup group={primaryLinkGroup} id="primary-footer-navigation" label="Primary footer navigation" columns={{ lg: 2 }} />
                </Column>
                { secondaryLinkGroupListPrimary.map((group) => (
                  <Column key={group.id} spans={{ lg: 6 }}>
                    <FooterLinkGroup group={group} id={`secondary-footer-navigation-${group.id}`} label={`${group.title} footer navigation`} />
                  </Column>
                )) }
              </>
            </Grid>
          </Column>
          <Column spans={{ lg: 1 }} className="hidden lg:block">&nbsp;</Column>
        </Grid>
        <Grid gap="none">
          <Column spans={{ lg: 1 }} className="hidden lg:block">&nbsp;</Column>
          <Column spans={{ lg: 5 }} className="hidden lg:block">
            <FooterLinkGroup
              group={primaryLinkGroup}
              id="primary-footer-navigation"
              label="Primary footer navigation" columns={{ lg: 2 }}
            />
          </Column>
          <Column spans={{ lg: 1 }} className="hidden lg:block">&nbsp;</Column>
          <Column spans={{ lg: 4 }}>
            <Grid gap="none">
              { secondaryLinkGroupListSecondary.map((group) => (
                <Column key={group.id} spans={{ lg: 6 }}>
                  <FooterLinkGroup group={group} id={`secondary-footer-navigation-${group.id}`} label={`${group.title} footer navigation`} />
                </Column>
              )) }
            </Grid>
          </Column>
          <Column spans={{ lg: 1 }} className="hidden lg:block">&nbsp;</Column>
        </Grid>
      </Container>
      <Spacer size="xl" />
      <Container>
        <div className="mx-auto max-w-[342px]">
          <CountryToggle />
        </div>
      </Container>
      <Spacer size="xl" />
      <Grid>
        <Column direction="row" justify="center">
          <Text tag="div" className="w-full">
            <Logo />
          </Text>
        </Column>
      </Grid>
      <Container>
        <nav id="tertiary-footer-navigation" aria-label="Tertiary footer navigation">
          <List direction="row" justify="center" align="center">
            <ListItem className="mb-4 mx-4">
              <Text tag="span" size="xs">
                &copy;KatKin
                { " " }
                { new Date().getFullYear() }
              </Text>
            </ListItem>
            { tertiaryLinks.map((link) => <FooterLink key={link.id} link={link} size="xs" className="mx-4" />) }
          </List>
        </nav>
      </Container>
      <Spacer size="4xl" />
    </footer>
  );
};

export default Footer;
